import React, { useState, useEffect, useCallback } from 'react';
import { useUser } from './UserProvider';
import useAdminVerification from '../hooks/useAdminVerification';
import { hashPassword } from '../utils/crypto';
import '../css.css';

const Comments = ({ pageName }) => {
  const { user, isAuthenticated } = useUser();
  const { isAdminVerified } = useAdminVerification();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState({
    nickname: '',
    password: '',
    content: '',
    parentId: null // 답글 대상 댓글 ID
  });
  const [replyingTo, setReplyingTo] = useState(null); // 현재 답글 작성 중인 댓글 ID
  const [editingComment, setEditingComment] = useState(null); // 현재 수정 중인 댓글 ID
  const [editContent, setEditContent] = useState(''); // 수정할 댓글 내용
  const [editPassword, setEditPassword] = useState(''); // 수정용 비밀번호
  const [deletePassword, setDeletePassword] = useState('');
  const [deletingCommentId, setDeletingCommentId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  // 알림 모달 state
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('info'); // 'info', 'error', 'success'

  // 로그인 상태에 따른 초기값 설정
  useEffect(() => {
    if (isAuthenticated() && user) {
      setNewComment(prev => ({
        ...prev,
        nickname: user.username
      }));
    } else {
      setNewComment(prev => ({
        ...prev,
        nickname: 'ㅇㅇ'
      }));
    }
  }, [user, isAuthenticated]);

  // 알림 모달 표시 함수
  const showAlert = useCallback((message, type = 'info') => {
    setAlertMessage(message);
    setAlertType(type);
    setShowAlertModal(true);
  }, []);

  const closeAlert = useCallback(() => {
    setShowAlertModal(false);
    setAlertMessage('');
  }, []);

  // 클라이언트측 입력 검증
  const validateInput = (input) => {
    // 기본 정리
    const cleaned = input.trim();
    
    // XSS 위험이 높은 패턴만 차단
    const xssPatterns = [
      /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,  // script 태그
      /<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi,  // iframe 태그
      /javascript:/gi,                                         // javascript: 프로토콜
      /on\w+\s*=/gi,                                          // 이벤트 핸들러
      /eval\s*\(/gi,                                          // eval 함수
      /expression\s*\(/gi,                                    // CSS expression
    ];
    
    for (const pattern of xssPatterns) {
      if (pattern.test(cleaned)) {
        throw new Error('보안상 위험한 내용이 감지되었습니다.');
      }
    }
    
    return cleaned;
  };

  // 댓글 목록 불러오기
  const fetchComments = useCallback(async (bypassCache = false) => {
    try {
      const timestamp = bypassCache ? `?t=${Date.now()}` : '';
      const response = await fetch(`/api/comments/${pageName}${timestamp}`, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setComments(data);
      } else {
        // 서버 에러 시 사용자에게 알림
        try {
          const errorData = await response.json();
          const errorMessage = errorData.error || '댓글을 불러오는데 실패했습니다.';
          
          switch (response.status) {
            case 400:
              console.error('댓글 로딩 오류:', errorMessage);
              break;
            case 404:
              console.log('해당 페이지에 댓글이 없습니다.');
              setComments([]);
              break;
            case 500:
              console.error('서버 오류로 댓글을 불러올 수 없습니다.');
              showAlert('댓글을 불러오는 중 오류가 발생했습니다.', 'error');
              break;
            default:
              console.error('댓글 로딩 실패:', errorMessage);
          }
        } catch (parseError) {
          console.error('댓글 로딩 중 응답 파싱 오류');
        }
      }
    } catch (error) {
      // 네트워크 오류 등
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        console.error('네트워크 연결 오류로 댓글을 불러올 수 없습니다.');
        showAlert('네트워크 연결을 확인해주세요.', 'error');
      } else {
        console.error('댓글 로딩 중 예상치 못한 오류:', error.message);
      }
    } finally {
      setLoading(false);
    }
  }, [pageName, showAlert]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  // 댓글 작성
  const handleSubmitComment = async (e) => {
    e.preventDefault();
    
    // 로그인하지 않은 사용자의 경우 비밀번호 필수
    if (!isAuthenticated() && (!newComment.password.trim())) {
      showAlert('비밀번호를 입력해주세요.', 'error');
      return;
    }
    
    if (!newComment.nickname.trim() || !newComment.content.trim()) {
      showAlert('닉네임과 댓글 내용을 입력해주세요.', 'error');
      return;
    }

    // 클라이언트측 입력 검증
    try {
      const validatedNickname = validateInput(newComment.nickname);
      const validatedContent = validateInput(newComment.content);
      
      if (validatedNickname.length > 20) {
        showAlert('닉네임은 20자 이하로 입력해주세요.', 'error');
        return;
      }
      
      if (validatedContent.length > 1000) {
        showAlert('댓글은 1000자 이하로 입력해주세요.', 'error');
        return;
      }
      
    } catch (error) {
      showAlert(error.message, 'error');
      return;
    }

    try {
      const requestBody = {
        pageName,
        nickname: newComment.nickname,
        content: newComment.content
      };

      // 답글인 경우 parentId 추가
      if (newComment.parentId) {
        requestBody.parentId = newComment.parentId;
      }

      // 로그인하지 않은 사용자만 비밀번호 포함
      if (!isAuthenticated()) {
        // 비밀번호 암호화 (PasswordReset.js와 동일한 방식)
        const hashedPassword = hashPassword(newComment.password, newComment.nickname, 'comment');
        requestBody.password = hashedPassword;
      }

      const headers = {
        'Content-Type': 'application/json',
      };

      // 로그인한 사용자는 JWT 토큰 포함
      if (isAuthenticated()) {
        headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      }

      const response = await fetch(`/api/comments`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        setNewComment(prev => ({ 
          nickname: isAuthenticated() ? user.username : 'ㅇㅇ', 
          password: '', 
          content: '',
          parentId: null
        }));
        setReplyingTo(null); // 답글 모드 해제
        // 캐시를 우회하여 댓글 목록 새로고침
        await fetchComments(true);
        showAlert('댓글이 성공적으로 작성되었습니다.', 'success');
      } else {
        // 서버에서 반환된 상세 에러 메시지 처리
        try {
          const errorData = await response.json();
          const errorMessage = errorData.error || '댓글 작성에 실패했습니다.';
          
          // HTTP 상태 코드별 상세 메시지
          switch (response.status) {
            case 400:
              showAlert(`입력 오류: ${errorMessage}`, 'error');
              break;
            case 401:
              showAlert('로그인이 필요합니다.', 'error');
              break;
            case 403:
              showAlert('권한이 없습니다. 다시 로그인해주세요.', 'error');
              break;
            case 429:
              showAlert('요청이 너무 많습니다. 잠시 후 다시 시도해주세요.', 'error');
              break;
            case 500:
              showAlert('서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.', 'error');
              break;
            case 503:
              showAlert('서비스를 일시적으로 사용할 수 없습니다. 잠시 후 다시 시도해주세요.', 'error');
              break;
            default:
              showAlert(`댓글 작성 실패: ${errorMessage}`, 'error');
          }
        } catch (parseError) {
          // JSON 파싱 실패 시
          showAlert(`서버 응답 오류 (${response.status}): 잠시 후 다시 시도해주세요.`, 'error');
        }
      }
    } catch (error) {
      // 네트워크 오류 등 fetch 자체 실패
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        showAlert('네트워크 연결을 확인해주세요.', 'error');
      } else if (error.name === 'AbortError') {
        showAlert('요청이 취소되었습니다.', 'error');
      } else if (error.message.includes('timeout')) {
        showAlert('요청 시간이 초과되었습니다. 다시 시도해주세요.', 'error');
      } else {
        showAlert(`댓글 작성 중 오류가 발생했습니다: ${error.message}`, 'error');
      }
    }
  };

  // 댓글 삭제
  const handleDeleteComment = async () => {
    const comment = comments.find(c => c.id === deletingCommentId);
    const isMyComment = isAuthenticated() && user && comment?.user_id === user.id;
    const isAdmin = isAuthenticated() && user && isAdminVerified;
    
    if (!isMyComment && !isAdmin && !deletePassword.trim()) {
      showAlert('비밀번호를 입력해주세요.', 'error');
      return;
    }

    try {
      const headers = {
        'Content-Type': 'application/json',
      };

      const requestBody = {};

      if (isMyComment || isAdmin) {
        // 로그인한 사용자의 본인 댓글 삭제 또는 관리자 삭제
        headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      } else {
        // 비로그인 사용자 또는 타인 댓글 삭제 - 비밀번호 암호화
        const hashedPassword = hashPassword(deletePassword, comment.nickname, 'comment');
        requestBody.password = hashedPassword;
      }

      const response = await fetch(`/api/comments/${deletingCommentId}`, {
        method: 'DELETE',
        headers: headers,
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        // 캐시를 우회하여 댓글 목록 새로고침
        await fetchComments(true);
        setShowDeleteModal(false);
        setDeletePassword('');
        setDeletingCommentId(null);
        showAlert('댓글이 성공적으로 삭제되었습니다.', 'success');
      } else {
        // 서버에서 반환된 상세 에러 메시지 처리
        try {
          const errorData = await response.json();
          const errorMessage = errorData.error || '댓글 삭제에 실패했습니다.';
          
          // HTTP 상태 코드별 상세 메시지
          switch (response.status) {
            case 400:
              showAlert(`입력 오류: ${errorMessage}`, 'error');
              break;
            case 401:
              showAlert('비밀번호가 일치하지 않습니다.', 'error');
              break;
            case 403:
              showAlert('권한이 없습니다. 본인의 댓글만 삭제할 수 있습니다.', 'error');
              break;
            case 404:
              showAlert('삭제하려는 댓글을 찾을 수 없습니다.', 'error');
              break;
            case 429:
              showAlert('요청이 너무 많습니다. 잠시 후 다시 시도해주세요.', 'error');
              break;
            case 500:
              showAlert('서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.', 'error');
              break;
            default:
              showAlert(`댓글 삭제 실패: ${errorMessage}`, 'error');
          }
        } catch (parseError) {
          // JSON 파싱 실패 시
          showAlert(`서버 응답 오류 (${response.status}): 잠시 후 다시 시도해주세요.`, 'error');
        }
      }
    } catch (error) {
      // 네트워크 오류 등 fetch 자체 실패
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        showAlert('네트워크 연결을 확인해주세요.', 'error');
      } else if (error.name === 'AbortError') {
        showAlert('요청이 취소되었습니다.', 'error');
      } else if (error.message.includes('timeout')) {
        showAlert('요청 시간이 초과되었습니다. 다시 시도해주세요.', 'error');
      } else {
        showAlert(`댓글 삭제 중 오류가 발생했습니다: ${error.message}`, 'error');
      }
    }
  };

  // 답글 작성 시작
  const startReply = (commentId, authorNickname) => {
    setReplyingTo(commentId);
    setNewComment(prev => ({
      ...prev,
      parentId: commentId,
      content: `@${authorNickname} `
    }));
    
    // 댓글 작성 폼으로 스크롤
    const commentForm = document.querySelector('.comment-form');
    if (commentForm) {
      commentForm.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // 답글 작성 취소
  const cancelReply = () => {
    setReplyingTo(null);
    setNewComment(prev => ({
      ...prev,
      parentId: null,
      content: prev.content.replace(/^@\S+\s/, '')
    }));
  };

  // 댓글 수정 시작
  const startEdit = (comment) => {
    setEditingComment(comment.id);
    setEditContent(comment.content);
    setEditPassword('');
  };

  // 댓글 수정 취소
  const cancelEdit = () => {
    setEditingComment(null);
    setEditContent('');
    setEditPassword('');
  };

  // 댓글 수정 처리
  const handleEditComment = async (commentId) => {
    const comment = comments.find(c => c.id === commentId);
    const isMyComment = isAuthenticated() && user && comment?.user_id === user.id;
    
    if (!isMyComment && !editPassword.trim()) {
      showAlert('비밀번호를 입력해주세요.', 'error');
      return;
    }

    if (!editContent.trim()) {
      showAlert('댓글 내용을 입력해주세요.', 'error');
      return;
    }

    // 클라이언트측 입력 검증
    try {
      const validatedContent = validateInput(editContent);
      
      if (validatedContent.length > 1000) {
        showAlert('댓글은 1000자 이하로 입력해주세요.', 'error');
        return;
      }
      
    } catch (error) {
      showAlert(error.message, 'error');
      return;
    }

    try {
      const headers = {
        'Content-Type': 'application/json',
      };

      const requestBody = {
        content: editContent
      };

      if (isMyComment) {
        // 로그인한 사용자의 본인 댓글 수정
        headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      } else {
        // 비로그인 사용자 또는 타인 댓글 수정 - 비밀번호 암호화
        const hashedPassword = hashPassword(editPassword, comment.nickname, 'comment');
        requestBody.password = hashedPassword;
      }

      const response = await fetch(`/api/comments/${commentId}`, {
        method: 'PUT',
        headers: headers,
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        // 캐시를 우회하여 댓글 목록 새로고침
        await fetchComments(true);
        setEditingComment(null);
        setEditContent('');
        setEditPassword('');
        showAlert('댓글이 성공적으로 수정되었습니다.', 'success');
      } else {
        // 서버에서 반환된 상세 에러 메시지 처리
        try {
          const errorData = await response.json();
          const errorMessage = errorData.error || '댓글 수정에 실패했습니다.';
          
          // HTTP 상태 코드별 상세 메시지
          switch (response.status) {
            case 400:
              showAlert(`입력 오류: ${errorMessage}`, 'error');
              break;
            case 401:
              showAlert('비밀번호가 일치하지 않습니다.', 'error');
              break;
            case 403:
              showAlert('권한이 없습니다. 본인의 댓글만 수정할 수 있습니다.', 'error');
              break;
            case 404:
              showAlert('수정하려는 댓글을 찾을 수 없습니다.', 'error');
              break;
            case 429:
              showAlert('요청이 너무 많습니다. 잠시 후 다시 시도해주세요.', 'error');
              break;
            case 500:
              showAlert('서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.', 'error');
              break;
            default:
              showAlert(`댓글 수정 실패: ${errorMessage}`, 'error');
          }
        } catch (parseError) {
          // JSON 파싱 실패 시
          showAlert(`서버 응답 오류 (${response.status}): 잠시 후 다시 시도해주세요.`, 'error');
        }
      }
    } catch (error) {
      // 네트워크 오류 등 fetch 자체 실패
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        showAlert('네트워크 연결을 확인해주세요.', 'error');
      } else if (error.name === 'AbortError') {
        showAlert('요청이 취소되었습니다.', 'error');
      } else if (error.message.includes('timeout')) {
        showAlert('요청 시간이 초과되었습니다. 다시 시도해주세요.', 'error');
      } else {
        showAlert(`댓글 수정 중 오류가 발생했습니다: ${error.message}`, 'error');
      }
    }
  };

  // 댓글을 계층 구조로 변환
  const organizeComments = (comments) => {
    const commentMap = {};
    const rootComments = [];
    
    // 모든 댓글을 맵에 저장
    comments.forEach(comment => {
      commentMap[comment.id] = { ...comment, replies: [] };
    });
    
    // 댓글을 부모-자식 관계로 정리
    comments.forEach(comment => {
      if (comment.parent_id) {
        const parent = commentMap[comment.parent_id];
        if (parent) {
          parent.replies.push(commentMap[comment.id]);
        } else {
          // 부모 댓글이 삭제된 경우, 답글을 루트 레벨로 이동
          // reply_depth가 이미 1 감소되어 있으므로 그대로 사용
          rootComments.push(commentMap[comment.id]);
        }
      } else {
        // 모든 원댓글을 루트 댓글로 추가 (삭제된 댓글도 포함)
        // 서버에서 이미 답글이 있는 삭제된 댓글은 "삭제된 댓글입니다"로 변경됨
        rootComments.push(commentMap[comment.id]);
      }
    });
    
    return rootComments;
  };

  // 삭제 모달 열기
  const openDeleteModal = (commentId) => {
    setDeletingCommentId(commentId);
    setShowDeleteModal(true);
  };

  // 삭제 모달 닫기
  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeletePassword('');
    setDeletingCommentId(null);
  };

  // 시간 포맷팅
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="comments-loading">댓글을 불러오는 중...</div>;
  }

  return (
    <div className="comments-section" id="comments-section">
      <div className="comments-container">
        <div className="comments-header">
          <h3 className="comments-title">
            💬 댓글 
            <span className="comments-count">{comments.length}</span>
          </h3>
        </div>
        <div className="comments-content">
      
      {/* 댓글 목록 */}
      <div className="comments-list">
        {comments.length === 0 ? (
          <div className="no-comments">
            <div className="no-comments-icon">💬</div>
            <p>첫 번째 댓글을 작성해보세요!</p>
          </div>
        ) : (
          organizeComments(comments).map((comment) => (
            <div key={comment.id}>
              {/* 원댓글 또는 부모가 삭제된 답글 */}
              <div className={`comment-item ${comment.parent_id ? 'orphaned-reply' : ''} ${comment.is_deleted ? 'deleted-comment' : ''} ${isAuthenticated() && user && comment.user_id === user.id ? 'my-comment' : ''}`}>
                
                <div className="comment-header">
                  <div className="comment-author-info">
                    <div className="comment-author">
                      {comment.parent_id && (
                        <span className="reply-indicator">↳</span>
                      )}
                      {comment.nickname}
                      {isAuthenticated() && user && comment.user_id === user.id && (
                        <span className="my-comment-badge">내 댓글</span>
                      )}
                      {comment.author_is_admin && (
                        <span className="admin-badge">관리자</span>
                      )}
                    </div>
                    <div className="comment-meta">
                      <span className="comment-date">{formatDate(comment.created_at)}</span>
                      {comment.ip_address && (
                        <span className="comment-ip">{comment.ip_address}</span>
                      )}
                    </div>
                  </div>
                  <div className="comment-actions">
                    {/* 삭제되지 않았고 부모가 삭제된 답글이 아닌 경우에만 답글 버튼 표시 */}
                    {!comment.parent_id && !comment.is_deleted && (
                      <button 
                        className="reply-button"
                        onClick={() => startReply(comment.id, comment.nickname)}
                        title="답글 달기"
                      >
                        💬 답글
                      </button>
                    )}
                    {/* 삭제되지 않았고 비로그인 댓글이거나 본인이 작성한 로그인 댓글인 경우에만 수정 버튼 표시 */}
                    {!comment.is_deleted && (!comment.user_id || (isAuthenticated() && user && comment.user_id === user.id)) && (
                      <button 
                        className="edit-button"
                        onClick={() => startEdit(comment)}
                        title="댓글 수정"
                      >
                        ✏️ 수정
                      </button>
                    )}
                    {/* 삭제되지 않았고 비로그인 댓글이거나 본인이 작성한 로그인 댓글이거나 관리자인 경우에만 삭제 버튼 표시 */}
                    {!comment.is_deleted && (!comment.user_id || (isAuthenticated() && user && comment.user_id === user.id) || (isAuthenticated() && user && isAdminVerified)) && (
                      <button 
                        className="delete-button"
                        onClick={() => {
                          if (isAuthenticated() && user && (comment.user_id === user.id || isAdminVerified)) {
                            setDeletingCommentId(comment.id);
                            setDeletePassword('');
                            setShowDeleteModal(true);
                          } else {
                            openDeleteModal(comment.id);
                          }
                        }}
                        title="댓글 삭제"
                      >
                        ❌ 삭제
                      </button>
                    )}
                  </div>
                </div>
                {editingComment === comment.id ? (
                  <div className="edit-comment-form">
                    <textarea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      className="edit-textarea"
                      placeholder="댓글을 수정하세요..."
                      maxLength="1000"
                      rows="3"
                    />
                    {!isAuthenticated() || (user && comment.user_id !== user.id) ? (
                      <input
                        type="password"
                        placeholder="비밀번호"
                        value={editPassword}
                        onChange={(e) => setEditPassword(e.target.value)}
                        className="edit-password-input"
                      />
                    ) : null}
                    <div className="edit-actions">
                      <button 
                        className="edit-confirm-btn"
                        onClick={() => handleEditComment(comment.id)}
                      >
                        수정 완료
                      </button>
                      <button 
                        className="edit-cancel-btn"
                        onClick={cancelEdit}
                      >
                        취소
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="comments-section comment-content">{comment.content}</div>
                )}
              </div>
              
              {/* 답글들 - 원댓글(parent_id가 없는 경우)에만 표시 */}
              {!comment.parent_id && comment.replies && comment.replies.length > 0 && (
                <div className="replies-container">
                  {comment.replies.map((reply) => (
                    <div key={reply.id} className="reply-wrapper">
                      
                      <div className={`comment-item reply-item ${isAuthenticated() && user && reply.user_id === user.id ? 'my-comment' : ''}`}>
                        <div className="comment-header">
                          <div className="comment-author-info">
                            <div className="comment-author">
                              <span className="reply-indicator">↳</span>
                              {reply.nickname}
                              {isAuthenticated() && user && reply.user_id === user.id && (
                                <span className="my-comment-badge">내 댓글</span>
                              )}
                              {reply.author_is_admin && (
                                <span className="admin-badge">관리자</span>
                              )}
                            </div>
                            <div className="comment-meta">
                              <span className="comment-date">{formatDate(reply.created_at)}</span>
                              {reply.ip_address && (
                                <span className="comment-ip">{reply.ip_address}</span>
                              )}
                            </div>
                          </div>
                          <div className="comment-actions">
                            {/* 비로그인 답글이거나 본인이 작성한 로그인 답글인 경우에만 수정 버튼 표시 */}
                            {(!reply.user_id || (isAuthenticated() && user && reply.user_id === user.id)) && (
                              <button 
                                className="edit-button"
                                onClick={() => startEdit(reply)}
                                title="답글 수정"
                              >
                                ✏️ 수정
                              </button>
                            )}
                            {/* 비로그인 답글이거나 본인이 작성한 로그인 답글이거나 관리자인 경우에만 삭제 버튼 표시 */}
                            {(!reply.user_id || (isAuthenticated() && user && reply.user_id === user.id) || (isAuthenticated() && user && isAdminVerified)) && (
                              <button 
                                className="delete-button"
                                onClick={() => {
                                  if (isAuthenticated() && user && (reply.user_id === user.id || isAdminVerified)) {
                                    setDeletingCommentId(reply.id);
                                    setDeletePassword('');
                                    setShowDeleteModal(true);
                                  } else {
                                    openDeleteModal(reply.id);
                                  }
                                }}
                                title="답글 삭제"
                              >
                                ❌ 삭제
                              </button>
                            )}
                          </div>
                        </div>
                        {editingComment === reply.id ? (
                          <div className="edit-comment-form">
                            <textarea
                              value={editContent}
                              onChange={(e) => setEditContent(e.target.value)}
                              className="edit-textarea"
                              placeholder="답글을 수정하세요..."
                              maxLength="1000"
                              rows="3"
                            />
                            {!isAuthenticated() || (user && reply.user_id !== user.id) ? (
                              <input
                                type="password"
                                placeholder="비밀번호"
                                value={editPassword}
                                onChange={(e) => setEditPassword(e.target.value)}
                                className="edit-password-input"
                              />
                            ) : null}
                            <div className="edit-actions">
                              <button 
                                className="edit-confirm-btn"
                                onClick={() => handleEditComment(reply.id)}
                              >
                                수정 완료
                              </button>
                              <button 
                                className="edit-cancel-btn"
                                onClick={cancelEdit}
                              >
                                취소
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="comments-section comment-content">{reply.content}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* 댓글 작성 폼 */}
      <form onSubmit={handleSubmitComment} className="comment-form">
        {replyingTo && (
          <div className="reply-indicator">
            <span>💬 답글 작성 중</span>
            <button type="button" onClick={cancelReply} className="cancel-reply-btn">
              ✕ 취소
            </button>
          </div>
        )}
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">닉네임</label>
            <input
              type="text"
              placeholder="닉네임을 입력하세요"
              value={newComment.nickname}
              onChange={(e) => setNewComment({ ...newComment, nickname: e.target.value })}
              className="form-input"
              maxLength="20"
              disabled={isAuthenticated()} // 로그인된 사용자는 닉네임 변경 불가
              style={isAuthenticated() ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
            />
            {isAuthenticated() && (
              <small className="form-help-text">로그인된 계정의 아이디가 사용됩니다</small>
            )}
          </div>
          {!isAuthenticated() && (
            <div className="form-group">
              <label className="form-label">비밀번호</label>
              <input
                type="password"
                placeholder="비밀번호를 입력하세요"
                value={newComment.password}
                onChange={(e) => setNewComment({ ...newComment, password: e.target.value })}
                className="form-input"
              />
            </div>
          )}
        </div>
        <div className="form-group full-width">
          <label className="form-label">댓글 내용</label>
          <textarea
            placeholder="댓글을 입력하세요..."
            value={newComment.content}
            onChange={(e) => setNewComment({ ...newComment, content: e.target.value })}
            className="form-textarea"
            maxLength="500"
            rows="4"
          />
        </div>
        {!isAuthenticated() && (
          <div className="login-notice">
            <span>💡 </span>
            <span>로그인하면 더 편리하게 댓글을 작성할 수 있습니다!</span>
          </div>
        )}
        <button type="submit" className="submit-button">
          댓글 작성
        </button>
      </form>

      {/* 삭제 확인 모달 */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={closeDeleteModal}>
          <div className="modal-content comment-delete-modal" onClick={(e) => e.stopPropagation()}>
            <h4>댓글 삭제</h4>
            {isAuthenticated() && user && (comments.find(c => c.id === deletingCommentId)?.user_id === user.id || isAdminVerified) ? (
              <p>정말로 이 댓글을 삭제하시겠습니까?</p>
            ) : (
              <>
                <p>댓글을 삭제하려면 비밀번호를 입력하세요.</p>
                <input
                  type="password"
                  placeholder="비밀번호"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  className="delete-password-input"
                  onKeyPress={(e) => e.key === 'Enter' && handleDeleteComment()}
                />
              </>
            )}
            <div className="modal-buttons">
              <button onClick={handleDeleteComment} className="delete-confirm-btn">삭제</button>
              <button onClick={closeDeleteModal} className="delete-cancel-btn">취소</button>
            </div>
          </div>
        </div>
      )}

      {/* 알림 모달 */}
      {showAlertModal && (
        <div className="modal-overlay" onClick={closeAlert}>
          <div className={`modal-content alert-modal alert-${alertType}`} onClick={(e) => e.stopPropagation()}>
            <div className="alert-icon">
              {alertType === 'success' && '✅'}
              {alertType === 'error' && '❌'}
              {alertType === 'info' && 'ℹ️'}
            </div>
            <div className="alert-message">{alertMessage}</div>
            <button onClick={closeAlert} className="alert-ok-btn">확인</button>
          </div>
        </div>
      )}
        </div>
      </div>
    </div>
  );
};

export default Comments;
